// import warningImg from "@assets/images/warning.svg";
import Button from "@material-ui/core/Button";
import Card from "@material-ui/core/Card";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import { makeStyles } from "@material-ui/core/styles";
import { CardSpacer } from "@saleor/components/CardSpacer";
import CardTitle from "@saleor/components/CardTitle";
import { ConfirmButtonTransitionState } from "@saleor/components/ConfirmButton";
import Container from "@saleor/components/Container";
import Form from "@saleor/components/Form";
import Metadata from "@saleor/components/Metadata/Metadata";
import { MetadataFormData } from "@saleor/components/Metadata/types";
import PageBackNavigation from "@saleor/components/PageBackNavigation";
import PageBreadcrumbNavigation from "@saleor/components/PageBreadcrumbNavigation/PageBreadcrumbNavigation";
import PageHeader from "@saleor/components/PageHeader";
import SaveButtonBar from "@saleor/components/SaveButtonBar";
import SeoForm from "@saleor/components/SeoForm";
import { Tab, TabContainer } from "@saleor/components/Tab";
import { ProductErrorFragment } from "@saleor/fragments/types/ProductErrorFragment";
import { SubmitPromise } from "@saleor/hooks/useForm";
import { mapMetadataItemToInput } from "@saleor/utils/maps";
import useMetadataChangeTrigger from "@saleor/utils/metadata/useMetadataChangeTrigger";
import { RawDraftContentState } from "draft-js";
import React, { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import { maybe } from "../../../misc";
import { TabListActions } from "../../../types";
import CategoryDetailsForm from "../../components/CategoryDetailsForm";
import CategoryList from "../../components/CategoryList";
import CategoryStoreType from "../../components/CategoryStoreType";
import {
  CategoryDetails_category,
  CategoryDetails_category_children_edges_node,
  CategoryDetails_category_products_edges_node
} from "../../types/CategoryDetails";
import CategoryBackground from "../CategoryBackground";
import CategoryDeepLink from "../CategoryDeepLink";
import CategoryHeader from "../CategoryHeader";
import CategoryProducts from "../CategoryProducts";
const useStyles = makeStyles(
  theme => ({
    breadcrumbs: {
      [theme.breakpoints.down("sm")]: {
        fontSize: 20,
        // marginTop: theme.spacing(2),
        padding: 0
      },
      alignSelf: "flex-start",
      flex: 1,
      fontSize: 20,
      paddingBottom: "10px"
    },
    frmAction: {
      justifyContent: "flex-end"
    },
    frmBtn: {
      "&.active": {
        "& span": {
          color: "#fff"
        },
        backgroundColor: "#285BAD"
      },
      "&:hover": {
        "& span": {
          color: "#fff"
        },
        backgroundColor: "#285BAD"
      },
      color: theme.palette.primary.main,
      padding: "6px 30px"
    },
    frmTxt: {
      alignItems: "center",
      display: "flex",
      flexDirection: "column",
      fontSize: "18px",
      justifyContent: "center",
      minHeight: "100px"
    }
    // frmWarning: {
    //   margin: "0 0 15px",
    //   width: "80px"
    // }
  }),
  { name: "CategoryUpdatePage" }
);

export interface FormData extends MetadataFormData {
  backgroundImageAlt: string;
  description: RawDraftContentState;
  name: string;
  slug: string;
  color: string;
  seoTitle: string;
  iconImage: {
    alt: string;
    url: string;
  };
  seoDescription: string;
  smMarketsId: number;
  storeType: string;
}

export enum CategoryPageTab {
  categories = "categories",
  products = "products"
}

export interface CategoryUpdatePageProps
  extends TabListActions<"productListToolbar" | "subcategoryListToolbar"> {
  breadcrumbs: Array<{ name: string; id: string }>;
  changeTab: (index: CategoryPageTab) => void;
  currentTab: CategoryPageTab;
  errors: ProductErrorFragment[];
  id: string;
  isLoadingBreadcrumbs: boolean;
  disabled: boolean;
  category: CategoryDetails_category;
  products: CategoryDetails_category_products_edges_node[];
  subcategories: CategoryDetails_category_children_edges_node[];
  pageInfo: {
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
  saveButtonBarState: ConfirmButtonTransitionState;
  onIconUpload: (file: File) => void;
  onGenerateDeeplink: (id: string) => void;
  onClickBreadcrumb: (id: string) => void;
  onImageDelete: () => void;
  onSubmit: (data: FormData) => SubmitPromise;
  onImageUpload(file: File);
  onNextPage();
  onPreviousPage();
  onProductClick(id: string): () => void;
  onAddProduct();
  onBack();
  onDelete();
  onAddCategory();
  onCategoryClick(id: string): () => void;
}

const CategoriesTab = Tab(CategoryPageTab.categories);
const ProductsTab = Tab(CategoryPageTab.products);

export const CategoryUpdatePage: React.FC<CategoryUpdatePageProps> = ({
  changeTab,
  currentTab,
  category,
  disabled,
  errors,
  id,
  pageInfo,
  products,
  saveButtonBarState,
  subcategories,
  onIconUpload,
  onAddCategory,
  onAddProduct,
  onBack,
  onCategoryClick,
  onDelete,
  onNextPage,
  onPreviousPage,
  onProductClick,
  onSubmit,
  onImageDelete,
  onImageUpload,
  isChecked,
  productListToolbar,
  selected,
  subcategoryListToolbar,
  toggle,
  toggleAll,
  onGenerateDeeplink
}: CategoryUpdatePageProps) => {
  const navigate = useNavigate();
  const classes = useStyles("");
  const intl = useIntl();
  const {
    isMetadataModified,
    isPrivateMetadataModified,
    makeChangeHandler: makeMetadataChangeHandler
  } = useMetadataChangeTrigger();

  const initialData: FormData = category
    ? {
        backgroundImageAlt: maybe(() => category.backgroundImage.alt, ""),
        color: maybe(() => category.color, ""),
        description: maybe(() => JSON.parse(category.descriptionJson)),
        iconImage: {
          alt: maybe(() => category.iconImage.alt, ""),
          url: maybe(() => category.iconImage.url, "")
        },
        metadata: category?.metadata?.map(mapMetadataItemToInput),
        name: category.name || "",
        privateMetadata: category?.privateMetadata?.map(mapMetadataItemToInput),
        seoDescription: category.seoDescription || "",
        seoTitle: category.seoTitle || "",
        slug: category?.slug || "",
        smMarketsId: category.smMarketsId,
        storeType: category.storeType || ""
      }
    : {
        backgroundImageAlt: "",
        color: "",
        description: "",
        iconImage: {
          alt: null,
          url: null
        },
        metadata: undefined,
        name: "",
        privateMetadata: undefined,
        seoDescription: "",
        seoTitle: "",
        slug: "",
        smMarketsId: null,
        storeType: ""
      };
  const [title, setTitle] = useState("");
  const [open, setOpen] = useState(false);

  const callback = (data, click) => {
    if (click) {
      const metadata = isMetadataModified ? data.metadata : undefined;
      const privateMetadata = isPrivateMetadataModified
        ? data.privateMetadata
        : undefined;

      onSubmit({
        ...data,
        metadata,
        privateMetadata
      });
    }
    setOpen(false);
  };
  const dialogConfirm = message => {
    setTitle(message);
    setOpen(true);
  };
  const handleSubmit = () => {
    dialogConfirm("Are you sure you want to save this category?");
  };

  const breadcrumbDirectory = [
    {
      name: "Categories",
      url: "/categories"
    },
    {
      name: category ? category.name : undefined
    }
  ];

  return (
    <>
      <Form onSubmit={handleSubmit} initial={initialData} confirmLeave>
        {({ data, change, submit, hasChanged }) => {
          const changeMetadata = makeMetadataChangeHandler(change);
          const blank = true;

          return (
            <Container>
              <PageBreadcrumbNavigation items={breadcrumbDirectory} />
              <div style={{ display: "flex", flexDirection: "row" }}>
                <div style={{ marginTop: "5px" }}>
                  <PageBackNavigation onBack={() => navigate(-1)} />
                </div>
                <PageHeader title={category ? category.name : undefined} />
              </div>
              <CategoryDetailsForm
                category={category}
                data={data}
                disabled={disabled}
                errors={errors}
                onChange={change}
              />
              <CardSpacer />
              <CategoryDeepLink
                linkId={category && category.id}
                linkUrl={category && category.deeplinkUrl}
                shortLinkUrl={category && category.shortDynamicLink}
                onGenerateDeeplink={onGenerateDeeplink}
              />
              <CardSpacer />
              <CategoryStoreType
                id={id}
                disabled={category && category.parent ? true : false}
                data={data}
                onChange={change}
              />
              <CardSpacer />
              <CategoryBackground
                data={data}
                onImageUpload={onImageUpload}
                onImageDelete={onImageDelete}
                image={maybe(() => category.backgroundImage)}
                onChange={change}
              />
              <CategoryHeader
                data={data}
                onChange={change}
                onIconUpload={file => onIconUpload(file)}
              />
              <CardSpacer />
              {!blank ? (
                <div>
                  <SeoForm
                    helperText={intl.formatMessage({
                      defaultMessage:
                        "Add search engine title and description to make this category easier to find"
                    })}
                    errors={errors}
                    title={data.seoTitle}
                    titlePlaceholder={data.name}
                    description={data.seoDescription}
                    descriptionPlaceholder={data.name}
                    slug={data.slug}
                    slugPlaceholder={data.name}
                    loading={!category}
                    onChange={change}
                    disabled={disabled}
                  />
                  <CardSpacer />
                  <Metadata data={data} onChange={changeMetadata} />
                </div>
              ) : null}
              <CardSpacer />
              <TabContainer>
                <CategoriesTab
                  isActive={currentTab === CategoryPageTab.categories}
                  changeTab={changeTab}
                >
                  <FormattedMessage
                    defaultMessage="Subcategories"
                    description="number of subcategories in category"
                  />
                </CategoriesTab>
                <ProductsTab
                  isActive={currentTab === CategoryPageTab.products}
                  changeTab={changeTab}
                >
                  <FormattedMessage
                    defaultMessage="Products"
                    description="number of products in category"
                  />
                </ProductsTab>
              </TabContainer>
              <CardSpacer />
              {currentTab === CategoryPageTab.categories && (
                <Card>
                  <CardTitle
                    title={intl.formatMessage({
                      defaultMessage: "All Subcategories",
                      description: "section header"
                    })}
                    toolbar={
                      <Button
                        id="createCategoryBtn"
                        color="primary"
                        variant="text"
                        onClick={onAddCategory}
                      >
                        <FormattedMessage
                          defaultMessage="Create subcategory"
                          description="button"
                        />
                      </Button>
                    }
                  />
                  <CategoryList
                    categories={subcategories}
                    disabled={disabled}
                    isChecked={isChecked}
                    isRoot={false}
                    pageInfo={pageInfo}
                    selected={selected}
                    sort={undefined}
                    toggle={toggle}
                    toggleAll={toggleAll}
                    toolbar={subcategoryListToolbar}
                    onNextPage={onNextPage}
                    onPreviousPage={onPreviousPage}
                    onRowClick={onCategoryClick}
                    onSort={() => undefined}
                  />
                </Card>
              )}
              {currentTab === CategoryPageTab.products && (
                <CategoryProducts
                  brands={null}
                  categoryName={maybe(() => category.name)}
                  products={products}
                  disabled={disabled}
                  pageInfo={pageInfo}
                  onNextPage={onNextPage}
                  onPreviousPage={onPreviousPage}
                  onRowClick={onProductClick}
                  onAdd={onAddProduct}
                  toggle={toggle}
                  toggleAll={toggleAll}
                  selected={selected}
                  isChecked={isChecked}
                  toolbar={productListToolbar}
                />
              )}
              <SaveButtonBar
                status={products && products.length ? "true" : null}
                onCancel={onBack}
                onDelete={onDelete}
                onSave={submit}
                state={saveButtonBarState}
                disabled={disabled || !hasChanged}
              />
              <Dialog
                open={open}
                onClose={() => setOpen(false)}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
              >
                <DialogContent className={classes.frmTxt}>
                  {/* <img
                    src={warningImg}
                    className={classes.frmWarning}
                    alt="Warning"
                  /> */}
                  {title}
                </DialogContent>
                <DialogActions className={classes.frmAction}>
                  <Button
                    className={classes.frmBtn}
                    id="btnNo"
                    color="primary"
                    type="button"
                    onClick={() => callback(data, false)}
                  >
                    No
                  </Button>
                  <Button
                    className={classes.frmBtn + " active"}
                    id="btnYes"
                    color="primary"
                    type="button"
                    onClick={() => callback(data, true)}
                  >
                    Yes
                  </Button>
                </DialogActions>
              </Dialog>
            </Container>
          );
        }}
      </Form>
    </>
  );
};
CategoryUpdatePage.displayName = "CategoryUpdatePage";
export default CategoryUpdatePage;
